import { Combobox, SelectOption, SelectSizeVariant } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, useState } from 'react';

import useSearchProjects from '@/api/queries/project/useSearchProjects';

type ProjectSelectProps = {
  className?: string;
  projectPublicId?: string;
  onChange: (projectPublicId?: string, details?: { reference: string }) => void;
  disabled?: boolean;
  sizeVariant?: SelectSizeVariant;
};

const DEBOUNCE_MS = 300;

const ProjectSelect: FC<ProjectSelectProps> = (props) => {
  const { className, projectPublicId, onChange, disabled, sizeVariant } = props;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [query]);

  const { projects } = useSearchProjects(debouncedQuery);

  const options = useMemo(() => {
    const opts: SelectOption<string, { customerName?: string }>[] = (
      projects ?? []
    ).map((project) => ({
      value: project.publicId,
      label: project.reference,
      metadata: {
        customerName: project.customer?.name ?? undefined,
      },
    }));

    return opts;
  }, [projects]);

  return (
    <Combobox
      className={className}
      disabled={disabled}
      onChange={(value) => {
        const publicId = value || undefined;
        const match = options.find((o) => o.value === publicId);
        onChange(publicId, match ? { reference: match.label } : undefined);
      }}
      options={options}
      value={projectPublicId}
      query={query}
      setQuery={setQuery}
      filterFunction={null}
      multiple={false}
      sizeVariant={sizeVariant}
      placeholder="Select a project"
      inputPlaceholder="Search projects..."
      CustomOption={ProjectOption}
      CustomSelected={({ selectedOptions }) => {
        const option = selectedOptions[0];
        if (!option) return null;
        return <ProjectOption option={option} />;
      }}
    />
  );
};

export default ProjectSelect;

const ProjectOption: FC<{
  option: SelectOption<string, { customerName?: string }>;
}> = ({ option }) => {
  return (
    <div className="w-full">
      <div className="flex items-baseline gap-2">
        <h4>{option.label}</h4>
        <span>{option.metadata?.customerName || '-'}</span>
      </div>
    </div>
  );
};
